import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { authClient, setPlatformToken, clearPlatformToken, getPlatformToken } from "@/lib/musicApi";

const PlatformAuthContext = createContext();

export function PlatformAuthProvider({ children }) {
  const [platformUser, setPlatformUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (!getPlatformToken()) { setLoading(false); return; }
    try {
      const { data } = await authClient.get("/subscriber/me");
      setPlatformUser(data.user || data);
    } catch {
      clearPlatformToken();
      setPlatformUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const { data } = await authClient.post("/subscriber/login", { email, password });
    if (data.requires_2fa) return data;
    if (data.token) { setPlatformToken(data.token); setPlatformUser(data.user); }
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await authClient.post("/subscriber/logout"); } catch {}
    clearPlatformToken();
    setPlatformUser(null);
  }, []);

  const status = platformUser?.subscription_status;
  // A scoped "subscriber" ban withholds the members area; the subscription itself is untouched.
  const membersBanned = (platformUser?.blocked_surfaces || []).includes("subscriber");
  const isSubscriber = (status === "active" || status === "trialing") && !membersBanned;
  const isDon = platformUser?.subscription_tier === "premium" || !!platformUser?.is_gifted;
  // Round 2 16c/16e: three states from the authoritative server-computed field.
  // "former" = ever subscribed but not current. Current + former get the
  // subscriber-style home/exploration and keep profile access (with limits);
  // "never" (or logged out) is treated as a guest on the platform.
  const subscriptionState = platformUser ? (platformUser.subscription_state || "never") : "guest";
  const isFormerSubscriber = subscriptionState === "former";
  const everSubscribed = subscriptionState === "current" || subscriptionState === "former";

  return (
    <PlatformAuthContext.Provider value={{ platformUser, loading, isSubscriber, membersBanned, isDon, subscriptionState, isFormerSubscriber, everSubscribed, login, logout, refresh: checkAuth }}>
      {children}
    </PlatformAuthContext.Provider>
  );
}

export const usePlatformAuth = () => useContext(PlatformAuthContext);
